import type { FarmCurrentWeather } from './farmCurrentWeather';
import { formatTempC } from './farmCurrentWeather';

export type HarvestCompareKind =
  | 'on_track'
  | 'earlier'
  | 'later'
  | 'estimate_only'
  | 'plan_only'
  | 'none';

export type HarvestCompare = {
  kind: HarvestCompareKind;
  headline: string;
  detail: string;
  expectedLabel: string;
  predictedLabel: string;
  deltaDays: number | null;
};

export type CropCalendarReportModel = {
  farmId: string;
  farmName: string;
  cropType: string;
  plantingMethod: string;
  sowingDate: string;
  plantingDate: string;
  expectedHarvestDate: string;
  predictedHarvestDate: string;
  croppingSeason: string;
  cropStatus: string;
  establishmentDate: string;
  endDate: string;
  summary: string;
  whereNow: string;
  inPlainWords: string;
  whatNext: string;
  aboutWhen: string;
  tip: string;
  howSure: string;
  greennessTrend: string;
  weatherNow: string;
  harvest: HarvestCompare;
  stagePeriods: Array<{
    stage: string;
    start: string;
    end: string;
    isCurrent: boolean;
  }>;
  observations: Array<{
    date: string;
    stage: string;
    label: string;
    ndvi: number;
  }>;
  greennessRows: Array<{ date: string; ndvi: number; cloudPct: number | null }>;
};

function parseIsoDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value).trim());
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(from: Date, days: number): Date {
  const d = new Date(from);
  d.setDate(d.getDate() + days);
  return d;
}

export function formatReportDate(value: string | null | undefined): string {
  const d = parseIsoDate(value);
  if (!d) return value ? String(value) : '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatReportDateShort(value: string | null | undefined): string {
  const d = parseIsoDate(value);
  if (!d) return value ? String(value) : '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function sameYear(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear();
}

export function formatReportDateRange(start: string, end?: string | null): string {
  const s = parseIsoDate(start);
  const e = parseIsoDate(end || '');
  if (!s && !e) return '';
  if (s && !e) return `${formatReportDate(start)} \u2013 ongoing`;
  if (!s && e) return formatReportDate(end);
  if (s && e && sameYear(s, e)) {
    return `${formatReportDateShort(start)} \u2013 ${formatReportDate(end)}`;
  }
  return `${formatReportDate(start)} \u2013 ${formatReportDate(end)}`;
}

export function greennessBand(ndvi: number): { label: 'Low' | 'Medium' | 'High'; pct: number } {
  const pct = Math.max(8, Math.min(100, Math.round(Number(ndvi) * 100)));
  if (ndvi < 0.35) return { label: 'Low', pct };
  if (ndvi < 0.6) return { label: 'Medium', pct };
  return { label: 'High', pct };
}

function stagesMatch(a: string, b: string): boolean {
  const n = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const left = n(a);
  const right = n(b);
  return Boolean(left && right && (left === right || left.includes(right) || right.includes(left)));
}

function toIsoLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function greennessTrendInWords(
  series: Array<{ date: string; ndvi: number }> | undefined
): string {
  if (!series || series.length < 2) {
    return '';
  }
  const sorted = [...series].sort((a, b) => a.date.localeCompare(b.date));
  if (sorted.length < 4) {
    const first = sorted[0].ndvi;
    const last = sorted[sorted.length - 1].ndvi;
    if (last > first * 1.08) return 'Greenness has been building so far this season.';
    if (last < first * 0.92) return 'Greenness has dipped compared to earlier readings.';
    return 'Greenness has been fairly steady so far.';
  }
  const mid = Math.floor(sorted.length / 2);
  const firstHalf = sorted.slice(0, mid);
  const secondHalf = sorted.slice(mid);
  const mean = (rows: Array<{ ndvi: number }>) =>
    rows.reduce((s, r) => s + r.ndvi, 0) / rows.length;
  const a = mean(firstHalf);
  const b = mean(secondHalf);
  if (b > a * 1.08) return 'The field looks greener than it did earlier in the season.';
  if (b < a * 0.92) return 'The field looks less green than it did earlier in the season.';
  return 'Greenness has stayed about the same recently.';
}

export function weatherNowInWords(weather: FarmCurrentWeather | null | undefined): string {
  if (!weather) return '';
  const temp = formatTempC(weather.temperature_c);
  const bits: string[] = [];
  if (weather.is_precipitating || weather.condition === 'rain' || weather.condition === 'drizzle') {
    bits.push('It is raining or recently wet');
  } else if (weather.condition === 'thunderstorm') {
    bits.push('There are storms nearby');
  } else if (weather.condition === 'clouds') {
    bits.push('The sky is cloudy');
  } else if (weather.condition === 'clear') {
    bits.push('The sky is clear');
  } else if (weather.description) {
    bits.push(weather.description);
  }
  if (temp) bits.push(`about ${temp}`);
  return bits.join(', ') + (bits.length ? '.' : '');
}

function extractPredictedHarvest(data: any, todayIso: string): string {
  const llm = data?.llm_analysis || {};
  const readout = data?.farmer_readout || {};
  const direct = String(
    readout.predicted_harvest_date || llm.predicted_harvest_date || ''
  ).trim();
  if (parseIsoDate(direct)) return direct.slice(0, 10);

  const periods: Array<{ stage?: string; tentative_start_date?: string; tentative_end_date?: string | null }> =
    llm.growth_stage_periods || [];
  const harvestRow = periods.find((p) => /harvest/i.test(String(p?.stage || '')));
  const fromPeriod = harvestRow?.tentative_start_date || harvestRow?.tentative_end_date || '';
  if (parseIsoDate(fromPeriod)) return String(fromPeriod).slice(0, 10);

  const next = llm.next_growth_stage_prediction || {};
  if (/harvest/i.test(String(next.name || ''))) {
    const lo = Number(next.estimated_days_min);
    const hi = Number(next.estimated_days_max);
    if (Number.isFinite(lo) && Number.isFinite(hi)) {
      const mid = Math.max(0, Math.round((lo + hi) / 2));
      const today = parseIsoDate(todayIso) || new Date();
      today.setHours(0, 0, 0, 0);
      return toIsoLocal(addDays(today, mid));
    }
  }
  return '';
}

export function harvestCompare(expected: string, predicted: string): HarvestCompare {
  const expectedDate = parseIsoDate(expected);
  const predictedDate = parseIsoDate(predicted);
  const expectedLabel = expectedDate ? formatReportDate(expected) : '';
  const predictedLabel = predictedDate ? formatReportDate(predicted) : '';

  if (!expectedDate && !predictedDate) {
    return {
      kind: 'none',
      headline: '',
      detail: '',
      expectedLabel: '',
      predictedLabel: '',
      deltaDays: null,
    };
  }

  if (expectedDate && !predictedDate) {
    return {
      kind: 'plan_only',
      headline: 'Your planned harvest',
      detail: `You planned harvest for ${expectedLabel}. We do not have a field estimate yet.`,
      expectedLabel,
      predictedLabel: '',
      deltaDays: null,
    };
  }

  if (!expectedDate && predictedDate) {
    return {
      kind: 'estimate_only',
      headline: 'Harvest estimate',
      detail: `You did not enter a harvest date. From the field, harvest looks around ${predictedLabel}.`,
      expectedLabel: '',
      predictedLabel,
      deltaDays: null,
    };
  }

  const deltaDays = Math.round(
    (predictedDate!.getTime() - expectedDate!.getTime()) / (1000 * 60 * 60 * 24)
  );
  const abs = Math.abs(deltaDays);
  const daysWord = abs === 1 ? 'day' : 'days';

  if (abs <= 7) {
    return {
      kind: 'on_track',
      headline: 'Close to your plan',
      detail: `Your planned harvest (${expectedLabel}) and the field estimate (${predictedLabel}) are about the same week.`,
      expectedLabel,
      predictedLabel,
      deltaDays,
    };
  }

  if (deltaDays < 0) {
    return {
      kind: 'earlier',
      headline: 'Looks earlier than your plan',
      detail: `The field looks ready about ${abs} ${daysWord} before your planned harvest (${expectedLabel}). Estimated around ${predictedLabel}.`,
      expectedLabel,
      predictedLabel,
      deltaDays,
    };
  }

  return {
    kind: 'later',
    headline: 'Looks later than your plan',
    detail: `The field looks about ${abs} ${daysWord} behind your planned harvest (${expectedLabel}). Estimated around ${predictedLabel}.`,
    expectedLabel,
    predictedLabel,
    deltaDays,
  };
}

export function buildCropCalendarReport(args: {
  data: any;
  farmId: string | number | undefined;
  farmName?: string | null;
  weather?: FarmCurrentWeather | null;
  cropStatus?: string | null;
}): CropCalendarReportModel {
  const data = args.data || {};
  const row = Array.isArray(data.data) ? data.data[0] : {};
  const readout = data.farmer_readout || {};
  const llm = data.llm_analysis || {};
  const ndvi: Array<{ date: string; ndvi: number; cloud_percentage?: number }> = data.ndvi_series || [];
  const expectedHarvestDate = String(
    data.expected_harvest_date || row.expected_harvest_date || ''
  ).trim();
  const todayIso = String(data.end_date || '').trim() || toIsoLocal(new Date());
  const predictedHarvestDate = extractPredictedHarvest(data, todayIso);
  const whereNow = String(
    readout.this_is_where_your_crop_is || llm.current_growth_stage?.name || ''
  ).trim();

  const periodsRaw: Array<{ stage?: string; tentative_start_date?: string; tentative_end_date?: string | null }> =
    llm.growth_stage_periods || [];
  const stagePeriods = periodsRaw
    .filter((p) => p?.stage)
    .map((p, i, arr) => {
      const stage = String(p.stage || '').trim();
      const isCurrent =
        (whereNow && stagesMatch(stage, whereNow)) ||
        (!whereNow && i === arr.length - 1);
      return {
        stage,
        start: String(p.tentative_start_date || '').trim(),
        end: String(p.tentative_end_date || '').trim(),
        isCurrent,
      };
    });

  const chart: Array<{ x?: string; y?: number; growth_stage?: string; label?: string | null }> =
    llm.chart_series || [];
  const observations = (chart.length
    ? chart.map((p) => ({
        date: String(p.x || '').trim(),
        stage: String(p.growth_stage || '').trim(),
        label: String(p.label || '').trim(),
        ndvi: Number(p.y),
      }))
    : ndvi.map((p) => ({
        date: String(p.date || '').trim(),
        stage: '',
        label: '',
        ndvi: Number(p.ndvi),
      }))
  )
    .filter((p) => p.date && Number.isFinite(p.ndvi))
    .slice(-8);

  const greennessRows = ndvi
    .filter((p) => p?.date && typeof p.ndvi === 'number')
    .slice(-8)
    .map((p) => ({
      date: String(p.date),
      ndvi: Number(p.ndvi),
      cloudPct: typeof p.cloud_percentage === 'number' ? p.cloud_percentage : null,
    }));

  return {
    farmId: String(args.farmId ?? data.farm_id ?? ''),
    farmName: String(args.farmName || '').trim() || 'This farm',
    cropType: String(data.crop_variety || row.crop_variety || 'Crop').trim(),
    plantingMethod: String(data.planting_method || row.planting_method || '').trim(),
    sowingDate: String(data.sowing_date || row.sowing_date || '').trim(),
    plantingDate: String(data.planting_date || row.planting_date || '').trim(),
    expectedHarvestDate,
    predictedHarvestDate,
    croppingSeason: String(data.cropping_season || row.cropping_season || '').trim(),
    cropStatus: String(args.cropStatus || data.crop_status || row.crop_status || '').trim(),
    establishmentDate: String(data.establishment_date || row.crop_establishment_date || '').trim(),
    endDate: String(data.end_date || '').trim(),
    summary: String(readout.summary || llm.summary || '').trim(),
    whereNow,
    inPlainWords: String(readout.in_plain_words || llm.current_growth_stage?.rationale || '').trim(),
    whatNext: String(readout.what_happens_next || llm.next_growth_stage_prediction?.name || '').trim(),
    aboutWhen: String(readout.about_when || '').trim(),
    tip: String(readout.tip || llm.next_growth_stage_prediction?.notes || '').trim(),
    howSure: String(readout.how_sure || '').trim(),
    greennessTrend: greennessTrendInWords(ndvi),
    weatherNow: weatherNowInWords(args.weather),
    harvest: harvestCompare(expectedHarvestDate, predictedHarvestDate),
    stagePeriods,
    observations,
    greennessRows,
  };
}

export function cropCalendarReportParagraphs(report: CropCalendarReportModel): string[] {
  const facts: string[] = [];
  facts.push(`${report.farmName} (field ID ${report.farmId || '—'}).`);
  const cropLine = [report.cropType, report.plantingMethod, report.croppingSeason ? `${report.croppingSeason} season` : '']
    .filter(Boolean)
    .join(' · ');
  if (cropLine) facts.push(cropLine + '.');
  const dates: string[] = [];
  if (report.sowingDate) dates.push(`sown ${formatReportDate(report.sowingDate)}`);
  if (report.plantingDate) dates.push(`planted in the field ${formatReportDate(report.plantingDate)}`);
  if (dates.length) facts.push(dates.join('; ') + '.');

  const now: string[] = [];
  if (report.summary) now.push(report.summary);
  if (report.whereNow) {
    now.push(
      report.howSure
        ? `Your crop looks to be at ${report.whereNow} (${report.howSure}).`
        : `Your crop looks to be at ${report.whereNow}.`
    );
  }
  if (report.inPlainWords) now.push(report.inPlainWords);

  const harvest: string[] = [];
  if (report.harvest.kind !== 'none') harvest.push(report.harvest.detail);

  const next: string[] = [];
  if (report.whatNext) {
    next.push(
      report.aboutWhen
        ? `Next: ${report.whatNext}. ${report.aboutWhen}`
        : `Next: ${report.whatNext}.`
    );
  }
  if (report.tip) next.push(report.tip);

  const extra: string[] = [];
  extra.push(report.greennessTrend);
  if (report.weatherNow) extra.push(`Weather now: ${report.weatherNow}`);

  return [...facts, ...now, ...harvest, ...next, ...extra].filter(Boolean);
}

export function buildPandoyCropCalendarPaste(report: CropCalendarReportModel): string {
  const body = cropCalendarReportParagraphs(report).join('\n');
  const harvestBits =
    report.harvest.kind === 'none'
      ? ''
      : `\nHarvest dates:\n- Your plan: ${report.harvest.expectedLabel || 'not entered'}\n- Field estimate: ${report.harvest.predictedLabel || 'not ready yet'}\n`;
  return (
    `Please help me further analyse this crop calendar for farm_id="${report.farmId}"` +
    `${report.farmName ? ` (${report.farmName})` : ''}.\n\n` +
    `Farmer report:\n${body}\n${harvestBits}\n` +
    `Use this information. You can also check weather forecast or field health for farm_id="${report.farmId}" if needed. ` +
    `Answer in plain language.`
  );
}
